/**
 * PromotionArchiveCard (extracted from ReleaseOpsTab.jsx in iter84.59.18)
 *
 * Operational mode (preview/site-admin): renders the live DB list of
 * promotions via `api.runbookListPromotions()` — backed by the
 * `/api/release-ops/runbook-archive` endpoint on production
 * (GitHub-mediated archive branch) and `/api/release-ops/promotions`
 * on preview (live DB). Shows in-progress + recent finalized +
 * cancelled rows. Each in-progress row exposes a Resume button so
 * the operator can pick up a stalled runbook walk.
 *
 * Archive mode (production / GitHub-mediated): receives `rows` as a prop
 * (finalized promotions persisted as JSON files in the GitHub repo's
 * `runbook-archive` branch — step 13 of the Deploy Workflow) and
 * shows each with a "View on GitHub →" link to the corresponding file.
 *
 * Touched in iter84.59.18 only to relocate — zero behavior change. The
 * `onResume` / `onReload` callbacks remain identical (parent owns
 * active-promotion state).
 */
import { useCallback, useEffect, useState } from "react";
import { History } from "lucide-react";
import { api } from "@/lib/api";
import { CollapsibleCard } from "@/components/site-admin/CollapsibleCard";

export function PromotionArchiveCard({ uiMode, rows, onResume, onReload }) {
    const isArchive = uiMode === "archive";
    const [liveRows, setLiveRows] = useState([]);
    const [loaded, setLoaded] = useState(false);

    const reload = useCallback(async () => {
        if (isArchive) return; // archive rows come from props
        try {
            const r = await api.runbookListPromotions();
            setLiveRows(r || []);
        } catch {
            /* silent */
        } finally {
            setLoaded(true);
        }
    }, [isArchive]);

    useEffect(() => {
        if (isArchive) {
            setLoaded(true);
            return;
        }
        reload();
    }, [isArchive, reload]);

    const display = isArchive ? rows : liveRows;
    const isEmpty = !display || display.length === 0;
    const title = isArchive ? "Promotion Archive (from GitHub)" : "Recent promotions";
    const badge = isArchive ? "Archive" : `${display?.length || 0}`;
    const badgeClass = "bg-zinc-700/30 text-zinc-300 border-zinc-600";

    return (
        <CollapsibleCard
            testId="promotion-archive-card"
            icon={History}
            title={title}
            badge={badge}
            badgeClass={badgeClass}
        >
            {!loaded ? (
                <p className="text-xs text-zinc-500 italic">Loading…</p>
            ) : isEmpty ? (
                <p className="text-xs text-zinc-500 italic">
                    {isArchive
                        ? "No finalized promotions in the GitHub archive yet."
                        : "No promotions started yet. Start one in Phase 0 above."}
                </p>
            ) : (
                <ul className="space-y-1" data-testid="promotion-archive-list">
                    {display.slice(0, 10).map((p) => {
                        const status = p.status || "finalized";
                        const key = p.id || p._github_filename || p.title;
                        const ghUrl = p._github_html_url;
                        return (
                            <li
                                key={key}
                                data-testid={`promotion-history-${key}`}
                                className="flex items-center justify-between text-[11px] py-1 border-b border-[#1a1a1a] last:border-b-0"
                            >
                                <span>
                                    <span
                                        className={`font-bold ${
                                            status === "finalized"
                                                ? "text-emerald-400"
                                                : status === "cancelled"
                                                    ? "text-rose-400"
                                                    : "text-amber-400"
                                        }`}
                                    >
                                        [{status}]
                                    </span>
                                    <span className="text-white ml-2">{p.title}</span>
                                    <span className="text-zinc-600 ml-2">
                                        {new Date(p.finalized_at || p.started_at).toLocaleString()}
                                    </span>
                                    {/* iter84.72 — Surface iter · SHA · Deployment ID
                                        so the archive answers "what exactly shipped?" */}
                                    {(() => {
                                        const iterMatch = (p.title || "").match(/iter[\w.]+/i);
                                        const sha = p.target_sha || p.sha_at_start;
                                        const chips = [
                                            iterMatch && { k: "iter", v: iterMatch[0] },
                                            sha && { k: "sha", v: String(sha).slice(0, 7) },
                                            p.emergent_deployment_id && { k: "deploy", v: p.emergent_deployment_id },
                                            /* iter86.36 — canon merge audit (PR # / merge SHA from Phase 3.3);
                                               strip any parenthesized GitHub URL so the chip stays compact */
                                            p.canon_merge_ref && { k: "pr", v: String(p.canon_merge_ref).replace(/\s*\(http[^)]*\)\s*/g, " ").trim().slice(0, 40) },
                                        ].filter(Boolean);
                                        if (chips.length === 0) return null;
                                        return (
                                            <span
                                                className="ml-2 inline-flex flex-wrap gap-1 align-middle"
                                                data-testid={`promotion-meta-${key}`}
                                            >
                                                {chips.map((c) => (
                                                    <span
                                                        key={c.k}
                                                        className="font-mono text-[9px] px-1.5 py-0.5 rounded border border-zinc-700 bg-zinc-900 text-zinc-300"
                                                    >
                                                        {c.k === "iter" ? c.v : `${c.k} ${c.v}`}
                                                    </span>
                                                ))}
                                            </span>
                                        );
                                    })()}
                                </span>
                                {isArchive && ghUrl ? (
                                    <a
                                        href={ghUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`open-github-${key}-btn`}
                                        className="brutal-btn-ghost text-[10px] py-1 px-2 text-volt hover:underline"
                                    >
                                        View on GitHub →
                                    </a>
                                ) : (!isArchive && status === "in_progress" && (
                                    <button
                                        onClick={() => {
                                            onResume?.(p.id);
                                            onReload?.();
                                        }}
                                        data-testid={`resume-${p.id}-btn`}
                                        className="brutal-btn-ghost text-[10px] py-1 px-2"
                                    >
                                        Resume
                                    </button>
                                ))}
                            </li>
                        );
                    })}
                </ul>
            )}
        </CollapsibleCard>
    );
}
